import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  RdHoursResponse,
  RdHoursSaveRequest,
  RdHoursWeekResponse,
  RdHoursWeekSaveRequest,
  RdHoursWeekSaveResponse,
} from "../../../shared/api-types.js";
import { authenticatedApi } from "./api.js";
import {
  classifyRdHoursError,
  rdHoursPath,
  rdHoursQueryKey,
  rdHoursWeekPath,
  rdHoursWeekQueryKey,
  type RdHoursErrorKind,
} from "./rd-hours-client.js";

// The Availability view's reads and saves (NO-878). The API answers no-store,
// so staleTime stays 0. Responses carry client names for booked visits: they
// live in the query cache only, never in storage, a URL, a log or a breadcrumb.

interface Enveloped<T> {
  data: T;
}

/** GET /api/rd-hours: the every-week hours. `enabled` is false with no RD chosen. */
export function useRdHours(rdId: string | undefined, enabled: boolean) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: rdHoursQueryKey(rdId),
    queryFn: async () => {
      const body = await authenticatedApi<Enveloped<RdHoursResponse>>(
        getToken,
        rdHoursPath(rdId)
      );

      return body.data;
    },
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  });
}

/** GET /api/rd-hours/weeks/:weekStart: one week as offered, with booked visits. */
export function useRdHoursWeek(
  rdId: string | undefined,
  weekStart: string | undefined,
  enabled: boolean
) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: rdHoursWeekQueryKey(rdId, weekStart ?? ""),
    queryFn: async () => {
      const body = await authenticatedApi<Enveloped<RdHoursWeekResponse>>(
        getToken,
        rdHoursWeekPath(weekStart ?? "", rdId)
      );

      return body.data;
    },
    enabled: enabled && weekStart !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  });
}

function putInit(body: unknown): RequestInit {
  return {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

/**
 * PUT /api/rd-hours. The mutation's error is the closed kind from
 * `classifyRdHoursError`, so callers branch on `error.kind`. A success writes the
 * fresh response into the read's cache, so a reload shows the saved state.
 */
export function useSaveRdHours(rdId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<RdHoursResponse, RdHoursErrorKind, RdHoursSaveRequest>({
    mutationFn: async (request) => {
      try {
        const body = await authenticatedApi<Enveloped<RdHoursResponse>>(
          getToken,
          rdHoursPath(rdId),
          putInit(request)
        );

        return body.data;
      } catch (error) {
        throw classifyRdHoursError(error);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(rdHoursQueryKey(rdId), data);
      // Any week read was drawn from the old every-week hours.
      void queryClient.invalidateQueries({ queryKey: ["rd-hours-week", rdId ?? null] });
    },
  });
}

/**
 * PUT /api/rd-hours/weeks/:weekStart. The answer carries only the change, so
 * the caller refetches the week (invalidated here) and rebuilds from the read.
 */
export function useSaveRdHoursWeek(
  rdId: string | undefined,
  weekStart: string | undefined
) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<RdHoursWeekSaveResponse, RdHoursErrorKind, RdHoursWeekSaveRequest>(
    {
      mutationFn: async (request) => {
        try {
          const body = await authenticatedApi<Enveloped<RdHoursWeekSaveResponse>>(
            getToken,
            rdHoursWeekPath(weekStart ?? "", rdId),
            putInit(request)
          );

          return body.data;
        } catch (error) {
          throw classifyRdHoursError(error);
        }
      },
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: rdHoursWeekQueryKey(rdId, weekStart ?? ""),
        }),
    }
  );
}

/** Refetch the week, for "Load the latest hours" after a week conflict. */
export function useReloadRdHoursWeek(
  rdId: string | undefined,
  weekStart: string | undefined
) {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: rdHoursWeekQueryKey(rdId, weekStart ?? ""),
    });
}

/** Refetch the every-week hours, for "Load the latest hours" without a body. */
export function useReloadRdHours(rdId: string | undefined) {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: rdHoursQueryKey(rdId) });
}
